import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	MessageSquare,
	Search,
	Plus,
	Calendar,
	Users,
	ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import MessageThreadOnly from "@/components/parent/MessageThreadOnly";
import DoulaSelectionModal from "@/components/ui/DoulaSelectionModal";

interface Conversation {
	id: string;
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string;
	};
	appointment: {
		appointment_date: string;
		status: string;
	};
	last_message?: {
		content: string;
		created_at: string;
	};
	unread_count: number;
	created_at: string;
}

interface Appointment {
	id: string;
	doula_user_id: string;
	doula_listing_id: string;
	appointment_date: string;
	status: string;
	duration_minutes: number;
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string;
		email: string;
	};
	doula_listing: {
		id: string;
		title: string;
		bio: string;
		location: string;
	};
}

const ParentMessages: React.FC = () => {
	const { profile: userProfile } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [conversations, setConversations] = useState<Conversation[]>([]);
	const [availableDoulas, setAvailableDoulas] = useState<Appointment[]>([]);
	const [selectedConversation, setSelectedConversation] = useState<
		string | null
	>(location.state?.conversationId || null);
	const [searchTerm, setSearchTerm] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [modalLoading, setModalLoading] = useState(false);

	useEffect(() => {
		if (userProfile) {
			loadConversations();
			loadAvailableDoulas();
		}
	}, [userProfile]);

	const loadConversations = async () => {
		if (!userProfile) return;

		try {
			// Use edge function to get conversations
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-conversations`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						action: "get_conversations",
						parentUserId: userProfile.id,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error loading conversations:", result.error);
				return;
			}

			setConversations(result.data || []);
		} catch (error) {
			console.error("Error loading conversations:", error);
		} finally {
			setLoading(false);
		}
	};

	const loadAvailableDoulas = async () => {
		if (!userProfile) return;

		try {
			// Use edge function to get appointments with proper doula information
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-appointments`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						parentUserId: userProfile.id,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error loading available doulas:", result.error);
				return;
			}

			setAvailableDoulas(result.data || []);
		} catch (error) {
			console.error("Error loading available doulas:", error);
		}
	};

	const handleSelectDoula = async (appointment: Appointment) => {
		if (!userProfile) return;

		try {
			setModalLoading(true);

			// Use edge function to check or create conversation
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-conversations`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						action: "check_or_create",
						parentUserId: userProfile.id,
						doulaUserId: appointment.doula_user_id,
						appointmentId: appointment.id,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error creating conversation:", result.error);
				return;
			}

			// Reload conversations and select the new one
			await loadConversations();
			setSelectedConversation(result.data.conversationId);
			setShowModal(false);
		} catch (error) {
			console.error("Error creating conversation:", error);
		} finally {
			setModalLoading(false);
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (days === 0) {
			return date.toLocaleTimeString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});
		} else if (days === 1) {
			return "Yesterday";
		} else if (days < 7) {
			return date.toLocaleDateString("en-US", { weekday: "short" });
		} else {
			return date.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
			});
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "accepted":
				return "bg-blue-100 text-blue-800";
			case "pending":
				return "bg-yellow-100 text-yellow-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading your messages...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-4">
							<Button
								variant="ghost"
								onClick={() => navigate("/parent-dashboard")}
								className="p-2"
							>
								<ArrowLeft className="h-5 w-5" />
							</Button>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 font-karla">
									Messages
								</h1>
								<p className="text-gray-600 font-karla">
									Connect with your doulas
								</p>
							</div>
						</div>

						<Button
							onClick={() => setShowModal(true)}
							className="bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
						>
							<Plus className="h-4 w-4 mr-2" />
							New Message
						</Button>
					</div>
				</motion.div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Conversations List */}
					<div className="lg:col-span-1">
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						>
							<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
								<CardHeader>
									<CardTitle className="text-xl font-karla flex items-center">
										<MessageSquare className="h-5 w-5 mr-2 text-primary-600" />
										Conversations
									</CardTitle>
								</CardHeader>
								<CardContent>
									{/* Search */}
									<div className="relative mb-4">
										<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
										<Input
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											placeholder="Search conversations..."
											className="pl-10"
										/>
									</div>

									{/* Conversations */}
									<div className="space-y-3">
										{conversations.length === 0 ? (
											<div className="text-center py-8">
												<MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
												<p className="text-gray-600 font-karla">
													No conversations yet
												</p>
												<p className="text-sm text-gray-500 font-karla mt-2">
													Start a conversation with a doula you have an
													appointment with
												</p>
											</div>
										) : (
											conversations
												.filter((conv) =>
													conv.doula_user.full_name
														.toLowerCase()
														.includes(searchTerm.toLowerCase()),
												)
												.map((conversation) => (
													<motion.div
														key={conversation.id}
														initial={{ opacity: 0, y: 10 }}
														animate={{ opacity: 1, y: 0 }}
														transition={{ duration: 0.6, delay: 0.2 }}
														onClick={() =>
															setSelectedConversation(conversation.id)
														}
														className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
															selectedConversation === conversation.id
																? "bg-primary-50 border-primary-200 border-2"
																: "bg-white/80 hover:bg-white border border-transparent"
														}`}
													>
														<div className="flex items-center space-x-3">
															<div className="relative">
																<img
																	src={
																		conversation.doula_user.avatar_url ||
																		"/placeholder.svg"
																	}
																	alt={conversation.doula_user.full_name}
																	className="w-12 h-12 rounded-full object-cover"
																/>
															</div>

															<div className="flex-1 min-w-0">
																<div className="flex items-center justify-between">
																	<h3 className="font-semibold text-gray-900 font-karla truncate">
																		{conversation.doula_user.full_name}
																	</h3>
																	<span className="text-xs text-gray-500 font-karla">
																		{conversation.last_message
																			? formatTime(
																					conversation.last_message.created_at,
																				)
																			: formatTime(conversation.created_at)}
																	</span>
																</div>

																<p className="text-sm text-gray-600 font-karla truncate">
																	{conversation.last_message
																		? conversation.last_message.content
																		: "No messages yet"}
																</p>

																<div className="flex items-center justify-between mt-2">
																	<Badge
																		variant="outline"
																		className={`text-xs ${getStatusColor(conversation.appointment.status)}`}
																	>
																		{conversation.appointment.status}
																	</Badge>

																	{conversation.unread_count > 0 && (
																		<div className="w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
																			{conversation.unread_count}
																		</div>
																	)}
																</div>
															</div>
														</div>
													</motion.div>
												))
										)}
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</div>

					{/* Messaging Interface */}
					<div className="lg:col-span-2">
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						>
							{selectedConversation ? (
								<MessageThreadOnly
									conversationId={selectedConversation}
									conversationData={conversations.find(
										(conv) => conv.id === selectedConversation,
									)}
								/>
							) : (
								<Card className="glass-card border-0 shadow-lg h-[600px] flex items-center justify-center">
									<div className="text-center">
										<MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
											Select a conversation
										</h3>
										<p className="text-gray-600 font-karla">
											Choose a doula to start messaging or schedule a meeting
										</p>
									</div>
								</Card>
							)}
						</motion.div>
					</div>
				</div>
			</div>

			{/* Doula Selection Modal */}
			<DoulaSelectionModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
				availableDoulas={availableDoulas}
				onSelectDoula={handleSelectDoula}
				loading={modalLoading}
			/>
		</div>
	);
};

export default ParentMessages;
